import { EmptyState } from "@/components/molecules/empty-state";
import type { SessionListItem } from "@/features/sessions";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useTranslation } from "@/features/utils/i18n";
import { useTitle } from "@/features/utils/use-title";
import { ChatsCircleIcon } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { ConversationList } from "./components/conversation-list";
import { ConversationView } from "./components/conversation-view";

/**
 * The call history, as a mail client: conversations on the left, the selected
 * one in full on the right.
 *
 * The selection is the URL (`/calls/session/:sessionId`), not component state,
 * so every link into a conversation — a dashboard row, the back-office, a link
 * pasted in a chat — opens the same two-pane view with that conversation
 * already open, and the browser's back button walks the reading history.
 *
 * It is the one page with no title bar of its own: a mailbox is read
 * full-height, and both panes carry their own header anyway (the search and
 * filters on the left, the customer and the call's metadata on the right).
 * The page title still reaches the browser tab, and the mobile layout's own
 * header, through `useTitle`.
 */
export function CallsPage() {
  const { t } = useTranslation("calls");
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const clientId = useCurrentClient()?.client?.id || "";
  useTitle(t("title"));

  const openSession = (session: SessionListItem) =>
    navigate(`/${clientId}/calls/session/${session.id}`);

  return (
    // Flush with the application frame, which is why the margins are
    // negative: the back-office layout pads every page with `px-4 md:px-8`,
    // and a mailbox inset inside that padding reads as a widget dropped on a
    // page rather than as the page. So this fills `SidebarInset` edge to edge
    // — hence no outer border either (the frame is the border), its `m-2` and
    // its `rounded-lg` in the height and the corners, and the mobile
    // `SiteHeader`'s 3rem below `md`.
    //
    // Flex, not grid: a grid row sizes itself to its content, and a long
    // conversation would push the thread's last messages under the input box
    // instead of scrolling inside the pane.
    <div className="-mx-4 flex h-[calc(100dvh-3rem)] overflow-hidden bg-white md:-mx-8 md:h-[calc(100dvh-1rem)] md:rounded-lg dark:bg-slate-950">
      {/* On a phone there is room for one pane: the list, until a conversation
          is opened, and then the conversation itself. */}
      <div
        className={`min-h-0 w-full flex-col border-r md:flex md:w-[22rem] md:shrink-0 lg:w-[26rem] ${sessionId ? "hidden" : "flex"}`}
      >
        <ConversationList selectedId={sessionId} onSelect={openSession} />
      </div>

      <div
        className={`min-h-0 min-w-0 flex-1 flex-col ${sessionId ? "flex" : "hidden md:flex"}`}
      >
        {sessionId ? (
          <ConversationView
            key={sessionId}
            sessionId={sessionId}
            onBack={() => navigate(`/${clientId}/calls`)}
          />
        ) : (
          <EmptyState
            compact
            icon={ChatsCircleIcon}
            title={t("list.selectPrompt")}
            className="h-full"
          />
        )}
      </div>
    </div>
  );
}
